// The widget input of a dashboard (WEB-DSH-VIEW-03, -04): many devices, each described by its own page document,
// with the latest values of every binding, and the members of each widget's source.
import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { apiClient, unwrap } from "../../api/client";
import { ApiError } from "../../api/problem";
import type { DeviceState, PointState, WidgetInput } from "../../widgets/generated/widget-fixture";
import type { Band } from "../../widgets/expected";
import type { Widget } from "../../widgets/types";
import { useLiveDevices, useLiveReady } from "../../live/LiveProvider";
import type { Device } from "../devices/queries";
import { expectedBands, profileQuery } from "../devices/page/profile";
import {
  devicePageQuery,
  latestQuery,
  LATEST_EVERY_MS,
  type Binding,
  type LatestResult,
  type WidgetPage,
} from "../devices/page/queries";
import { widgetInput } from "../devices/page/toWidgets";

/** Every datapoint binding of the dashboard, once, for POST /v1/latest. */
export function latestBindings(widgets: Widget[]): Binding[] {
  const seen = new Set<string>();
  const out: Binding[] = [];
  for (const w of widgets) {
    for (const b of w.bindings ?? []) {
      const k = `${b.device}/${b.datapoint ?? ""}`;
      if (b.datapoint && !seen.has(k)) {
        seen.add(k);
        out.push({ device: b.device, datapoint: b.datapoint });
      }
    }
  }
  return out;
}

/** The devices the widgets bind to, and a weather station's device (WSW-01), once. */
export function boundDevices(widgets: Widget[]): string[] {
  return [
    ...new Set(
      widgets.flatMap((w) => [
        ...(w.bindings ?? []).map((b) => b.device),
        ...(w.type === "weather_station" ? (w.source?.devices ?? []) : []),
      ]),
    ),
  ];
}

/** What a weather station reads: every value its device's page gives a station slot (WSW-01). */
export function stationBindings(widgets: Widget[], pages: Record<string, WidgetPage>): Binding[] {
  const devices = new Set(widgets.flatMap((w) => (w.type === "weather_station" ? (w.source?.devices ?? []) : [])));
  return [...devices].flatMap((device) =>
    Object.entries(pages[device]?.points ?? {})
      .filter(([, p]) => p.station_slot)
      .map(([datapoint]) => ({ device, datapoint })),
  );
}

/** A source's key for its member query: a space, a group, or a device list. */
export type SourceKey = { space: string } | { group: string } | { devices: string[] };

export function sourceKey(w: Widget): SourceKey | undefined {
  const s = w.source;
  if (!s) return undefined;
  if (s.space) return { space: s.space };
  if (s.group) return { group: s.group };
  return s.devices?.length ? { devices: [...s.devices] } : undefined;
}

/**
 * A member's state from its device record: `status`, and the position it reports itself (vision-api 0.21.0, a
 * tractor) or else the one a user placed it at, until a value says more.
 */
export function memberState(d: Device): DeviceState {
  const c = d.fixed_position?.coordinates;
  const placed =
    Array.isArray(c) && typeof c[0] === "number" && typeof c[1] === "number" ? { lat: c[1], lon: c[0] } : null;
  const position = d.position ? { lat: d.position.lat, lon: d.position.lon, at: d.position.at } : placed;
  const state = d.status === "online" || d.status === "sleeping" ? d.status : "offline";
  return { name: d.name ?? d.device_id, state, last_seen_at: d.last_seen_at ?? null, position };
}

/**
 * The shared input: each device's points and actions from its page and bands, the latest values over them, device
 * states from members first, then pages, then the latest result (the newest word).
 */
export function buildInput(
  pages: Record<string, WidgetPage>,
  bands: Record<string, Record<string, Band>>,
  latest: LatestResult | undefined,
  members: Device[],
): WidgetInput {
  const devices: Record<string, DeviceState> = {};
  for (const d of members) devices[d.id] = memberState(d);
  const points: Record<string, PointState> = {};
  const actions: NonNullable<WidgetInput["actions"]> = {};
  for (const [id, page] of Object.entries(pages)) {
    const one = widgetInput(id, page, latest, bands[id]);
    Object.assign(points, one.points);
    Object.assign(actions, one.actions);
    Object.assign(devices, one.devices);
  }
  return { devices, points, actions };
}

const memberQuery = (orgId: string, key: SourceKey) =>
  queryOptions({
    queryKey: ["org", orgId, "dashboard-source", key],
    queryFn: async (): Promise<Device[]> => {
      if ("devices" in key) {
        return Promise.all(
          key.devices.map((id) =>
            unwrap(apiClient("customers").GET("/v1/devices/{device_id}", { params: { path: { device_id: id } } })),
          ),
        );
      }
      const items: Device[] = [];
      let cursor: string | undefined;
      do {
        const filter = "space" in key ? { space_id: key.space } : { group_id: key.group };
        const page = await unwrap(
          apiClient("customers").GET("/v1/devices", { params: { query: { limit: 500, cursor, ...filter } } }),
        );
        items.push(...page.items);
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return items;
    },
    staleTime: 60_000,
  });

const alarmsQuery = (orgId: string, enabled: boolean) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/alarms"],
    queryFn: () => unwrap(apiClient("customers").GET("/v1/alarms", {})),
    enabled,
    refetchInterval: LATEST_EVERY_MS,
  });

const isGone = (error: unknown) => error instanceof ApiError && (error.status === 403 || error.status === 404);

/**
 * useDashboardInput over the saved widgets and, while the editor is open, its draft too, so a widget added in the
 * editor reads its value at once instead of after Save (owner's report #6). The editor reports its draft to `onDraft`.
 */
export function useEditedDashboardInput(orgId: string, widgets: Widget[], language: string) {
  const [draft, onDraft] = useState<Widget[]>();
  const all = useMemo(() => (draft ? [...widgets, ...draft] : widgets), [widgets, draft]);
  return { ...useDashboardInput(orgId, all, language), onDraft };
}

/** Everything a dashboard's widgets read, and each widget's own input (its source's members and their alarms). */
export function useDashboardInput(orgId: string, widgets: Widget[], language: string) {
  const ids = useMemo(() => boundDevices(widgets), [widgets]);
  const bindings = useMemo(() => latestBindings(widgets), [widgets]);
  const sources = useMemo(() => {
    const keys = new Map<string, SourceKey>();
    for (const w of widgets) {
      const k = sourceKey(w);
      if (k) keys.set(JSON.stringify(k), k);
    }
    return [...keys.values()];
  }, [widgets]);
  const pages = useQueries({ queries: ids.map((id) => devicePageQuery(orgId, id, language)) });
  const profiles = useQueries({ queries: ids.map((id) => profileQuery(orgId, id, language)) });
  const members = useQueries({ queries: sources.map((k) => memberQuery(orgId, k)) });
  // The latest values poll while any bound device is not followed live (WEB-F-05).
  const live = useLiveReady(ids);
  const memberIds = members.flatMap((m) => m.data?.map((d) => d.id) ?? []);
  useLiveDevices([...new Set([...ids, ...memberIds])]);
  const loaded: Record<string, WidgetPage> = {};
  ids.forEach((id, i) => {
    const p = pages[i]?.data;
    if (p) loaded[id] = p;
  });
  const stations = stationBindings(widgets, loaded).filter(
    (s) => !bindings.some((b) => b.device === s.device && b.datapoint === s.datapoint),
  );
  const latest = useQuery(latestQuery(orgId, stations.length ? [...bindings, ...stations] : bindings, live));
  const alarms = useQuery(
    alarmsQuery(
      orgId,
      widgets.some((w) => w.type === "alarm_list"),
    ),
  );

  // Built each render: the query results are new arrays every time, and the build is a few loops over the points.
  const byId: Record<string, WidgetPage> = {};
  const bands: Record<string, Record<string, Band>> = {};
  ids.forEach((id, i) => {
    const p = pages[i]?.data;
    if (p) byId[id] = p;
    bands[id] = expectedBands(profiles[i]?.data);
  });
  const input = buildInput(
    byId,
    bands,
    latest.data,
    members.flatMap((m) => m.data ?? []),
  );

  const inputFor = (w: Widget): WidgetInput => {
    const k = sourceKey(w);
    if (!k) return input;
    const i = sources.findIndex((s) => JSON.stringify(s) === JSON.stringify(k));
    const list = members[i]?.data?.map((d) => d.id) ?? ("devices" in k ? k.devices : []);
    const inScope = new Set(list);
    return {
      ...input,
      members: list,
      ...(alarms.data ? { alarms: alarms.data.items.filter((a) => inScope.has(a.device)) } : {}),
    };
  };

  return {
    input,
    inputFor,
    /** Bound devices whose page the API refuses: deleted, or no longer visible to this user (#9). */
    missing: new Set(ids.filter((_, i) => isGone(pages[i]?.error))),
    /** A bound device's page document, for what its controls send (device-controls). */
    docFor: (id: string) => profiles[ids.indexOf(id)]?.data,
    /** A bound device's page, for the plan a widget needs (plan-dashboards PD-12). */
    pageFor: (id: string | undefined) => (id ? byId[id] : undefined),
    /** A bound device's ActionInfo for a key, for a guarded command's confirm text. */
    infoFor: (id: string, key: string) => byId[id]?.actions[key],
    // Controls need each device's page document to know what to send, so the dashboard waits for those too.
    loading: pages.some((p) => p.isPending) || profiles.some((p) => p.isPending),
    error: latest.error,
    refetch: () => void latest.refetch(),
  };
}
